import React, { FC, useEffect, useState, useMemo, useRef } from "react"
import axios from "axios"
import { toast } from "@pma/hooks/use-toast"
import { SERVER_URL } from "@pma/utils/api-routes"
import { useAuth } from "@pma/stores/auth-store"
import { useQuery } from "@tanstack/react-query"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { ChevronDown, ChevronRight, Loader2, Search, Calendar, X } from "lucide-react"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import useTranslator from "@pma/hooks/use-translator"
import { cn } from "@pma/utils/helpers"
import { Switch } from "../common/switch"

// ------------------- Types -------------------
interface User {
  id: number
  name?: string
  email?: string
  organization_id?: number
  [k: string]: any
}

interface Project {
  id: number
  title?: string
  project_number?: string
  is_owner?: boolean
  is_member?: boolean
  selected?: boolean
  expanded?: boolean
  [k: string]: any
}

interface Task {
  id: number
  title?: string
  project_id?: number
  selected?: boolean
  [k: string]: any
}

interface Role {
  id: number
  name?: string
  selected?: boolean
  [k: string]: any
}

interface Props {
  open: boolean
  onClose: () => void
  sourceUser: User | null
}

// ------------------- Component -------------------
const TransferAccessModal: FC<Props> = ({ open, onClose, sourceUser }) => {
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"
  const [allUsers, setAllUsers] = useState<User[]>([])
  const [targetUserId, setTargetUserId] = useState<number | "">("")
  const [userSearchQuery, setUserSearchQuery] = useState("")
  const [showUserDropdown, setShowUserDropdown] = useState(false)
  const [projects, setProjects] = useState<Project[]>([])
  const [tasks, setTasks] = useState<Task[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [forcePin, setForcePin] = useState<boolean>(true)
  const [notificationTill, setNotificationTill] = useState<string | "">("")
  const [selectedDate, setSelectedDate] = useState<Date | null | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const { authConfig } = useAuth()
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const dropdownRef = useRef<HTMLDivElement>(null)

  const userAccessApi = async () => {
    const response = await axios.post(
      SERVER_URL + "/users/accesses",
      {
        user_id: sourceUser?.id,
        organization_id: authConfig?.last_organization_id,
      },
      {
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    const proj: Project[] = Array.isArray(response.data.projects)
      ? response.data.projects.map((p: any) => ({ ...p, selected: true, expanded: false }))
      : []

    const tsk: Task[] = Array.isArray(response.data.tasks)
      ? response.data.tasks.map((t: any) => ({ ...t, selected: true }))
      : []

    const rls: Role[] = Array.isArray(response.data.roles)
      ? response.data.roles.map((r: any) => ({ ...r, selected: true }))
      : []

    setProjects(proj)
    setTasks(tsk)
    setRoles(rls)
    return response.data
  }

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["workspace-contractors", sourceUser?.id || authConfig.id],
    queryFn: userAccessApi,
    enabled: open && !!sourceUser?.id,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  })

  // Check if user is admin or owner
  const isAdminOrOwner = useMemo(() => {
    const isOwner = authConfig?.id === authConfig?.workspace?.owner_id

    const currentOrganization = authConfig?.companies_of_user?.find(
      (company: any) => company.id === authConfig?.last_organization_id,
    )
    const isAdmin = currentOrganization?.role?.name === "Admin"

    return isOwner || isAdmin
  }, [authConfig])

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!userSearchQuery.trim()) return allUsers

    const query = userSearchQuery.toLowerCase()
    return allUsers.filter((u) => {
      const name = (u.name || "").toLowerCase()
      const email = (u.email || "").toLowerCase()
      return name.includes(query) || email.includes(query)
    })
  }, [allUsers, userSearchQuery])

  // Selected user
  const selectedUserName = useMemo(() => {
    const user = allUsers.find((u) => u.id === targetUserId)
    if (!user) return ""
    return user.name || user.email || `User ${user.id}`
  }, [targetUserId, allUsers])

  // Effect to refetch data when sourceUser changes
  useEffect(() => {
    if (open && sourceUser?.id) {
      refetch()
    }
  }, [sourceUser?.id, open, refetch])

  useEffect(() => {
    if (open && authConfig?.organizationUsers) {
      const users = authConfig.organizationUsers
      setAllUsers(users)
    }
  }, [authConfig, open])

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false)
      }
    }

    if (showUserDropdown) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [showUserDropdown])

  // Handle date change
  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date)
    if (date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, "0")
      const day = String(date.getDate()).padStart(2, "0")
      setNotificationTill(`${year}-${month}-${day}`)
    } else {
      setNotificationTill("")
    }
  }

  // Toggle project dropdown
  const toggleProjectExpanded = (projectId: number) => {
    setProjects((prev) =>
      prev.map((p) => (p.id === projectId ? { ...p, expanded: !p.expanded } : p)),
    )
  }

  // Check project checkbox state: 'all', 'some', 'none'
  const getProjectCheckState = (projectId: number) => {
    const projectTasks = tasks.filter((t) => t.project_id === projectId)

    if (projectTasks.length === 0) {
      const project = projects.find((p) => p.id === projectId)
      return project?.selected ? "all" : "none"
    }

    const selectedCount = projectTasks.filter((t) => t.selected).length
    if (selectedCount === 0) return "none"
    if (selectedCount === projectTasks.length) return "all"
    return "some"
  }

  // Get orphan tasks
  const orphanTasks = useMemo(() => {
    const projectIds = projects.map((p) => p.id)
    return tasks.filter((t) => !projectIds.includes(t.project_id || 0))
  }, [tasks, projects])

  // Toggle project checkbox
  const toggleProject = (projectId: number, checked: boolean) => {
    if (!isAdminOrOwner) return
    setProjects((prev) => prev.map((p) => (p.id === projectId ? { ...p, selected: checked } : p)))
    setTasks((prev) =>
      prev.map((t) => (t.project_id === projectId ? { ...t, selected: checked } : t)),
    )
  }

  const toggleTask = (taskId: number, checked: boolean) => {
    if (!isAdminOrOwner) return
    setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, selected: checked } : t)))

    const task = tasks.find((t) => t.id === taskId)
    if (task && task.project_id) {
      setTimeout(() => {
        const checkState = getProjectCheckState(task.project_id!)
        setProjects((prev) =>
          prev.map((p) =>
            p.id === task.project_id
              ? { ...p, selected: checkState === "all" || checkState === "some" }
              : p,
          ),
        )
      }, 0)
    }
  }

  const toggleRole = (roleId: number, checked: boolean) => {
    if (!isAdminOrOwner) return
    setRoles((prev) => prev.map((r) => (r.id === roleId ? { ...r, selected: checked } : r)))
  }

  const handleTransfer = async () => {
    if (!sourceUser) {
      toast({
        title: translate("Error"),
        description: translate("Source user not found"),
        variant: "error",
      })
      return
    }
    if (!targetUserId) {
      toast({
        title: translate("Error"),
        description: translate("Please select target user"),
        variant: "error",
      })
      return
    }

    setLoading(true)
    try {
      const selectedProjects = projects.filter((p) => p.selected).map((p) => p.id)
      const selectedTasks = tasks.filter((t) => t.selected).map((t) => t.id)
      const selectedRoles = roles.filter((r) => r.selected).map((r) => r.id)

      const notificationTimestamp = notificationTill ? new Date(notificationTill).getTime() : null

      await axios.post(
        `${SERVER_URL}/users/accesses_transfer`,
        {
          from_user_id: sourceUser.id,
          organization_id: sourceUser.organization_id || authConfig?.last_organization_id,
          to_user_id: targetUserId,
          projects_id: selectedProjects,
          tasks_id: selectedTasks,
          organization_roles_id: selectedRoles,
          pin_pinned: !!forcePin,
          notifications_from_period: notificationTimestamp,
        },
        {
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )

      toast({
        title: translate("Success"),
        description: translate("Access transferred successfully"),
        variant: "success",
      })
      onClose()
    } catch (err: any) {
      console.error("transfer error", err)
      toast({
        title: translate("Error"),
        description: err?.response?.data?.message || translate("Transfer failed"),
        variant: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  if (!open) return null

  const showLoader = isLoading || isFetching

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-6'>
      <style>{`
                input[type="checkbox"]:checked {
                    background-color: #6158fd !important;
                    border-color: #6158fd !important;
                }
                input[type="checkbox"]:indeterminate {
                    background-color: #6158fd !important;
                    border-color: #6158fd !important;
                    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 16 16'%3e%3cpath stroke='white' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M4 8h8'/%3e%3c/svg%3e") !important;
                }
                input[type="checkbox"]:focus {
                    --tw-ring-color: #6158fd !important;
                }
            `}</style>

      <div
        className='absolute inset-0 bg-black/50 backdrop-blur-sm'
        onClick={() => !loading && onClose()}
      />
      <div className='relative z-50 w-full max-w-5xl overflow-hidden rounded-xl bg-white shadow-2xl'>
        {/* Header */}
        <div className='flex items-center justify-between border-b bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-5'>
          <div>
            <h3 className='text-xl font-bold text-gray-800'>
              {translate("Transfer Access & Roles")}
            </h3>
            {sourceUser && (
              <p className='mt-1 text-sm text-gray-600'>
                {translate("From")}: <span className='font-semibold'>{sourceUser.name}</span>
              </p>
            )}
          </div>
          <button
            onClick={() => !loading && onClose()}
            aria-label='Close'
            className='rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700'
            disabled={loading}
          >
            <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
        </div>

        {/* Body with loader */}
        <div className='max-h-[70vh] space-y-6 overflow-y-auto p-6'>
          {showLoader ? (
            <div className='flex flex-col items-center justify-center py-16'>
              <Loader2 className='mb-4 h-12 w-12 animate-spin text-blue-600' />
              <p className='font-medium text-gray-600'>{translate("Loading access data")}...</p>
            </div>
          ) : (
            <>
              {/* Target User Selection */}
              <div className='rounded-lg border border-blue-200 bg-blue-50 p-4'>
                <label className='mb-2 block text-sm font-semibold text-gray-700'>
                  {translate("Transfer To (Target User) *")}
                </label>

                {/* Custom Dropdown */}
                <div className='relative' ref={dropdownRef}>
                  <button
                    type='button'
                    onClick={() => setShowUserDropdown(!showUserDropdown)}
                    disabled={!isAdminOrOwner}
                    className={`flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white p-3 text-left transition-all focus:border-transparent focus:ring-2 focus:ring-blue-500 ${
                      !isAdminOrOwner
                        ? "cursor-not-allowed bg-gray-100 opacity-60"
                        : "cursor-pointer"
                    }`}
                  >
                    <span className={targetUserId ? "text-gray-900" : "text-gray-500"}>
                      {selectedUserName || `${translate("Select Target User")}`}
                    </span>
                    <ChevronDown
                      className={`h-5 w-5 text-gray-400 transition-transform ${showUserDropdown ? "rotate-180" : ""}`}
                    />
                  </button>

                  {showUserDropdown && isAdminOrOwner && (
                    <div className='absolute z-10 mt-2 w-full overflow-hidden rounded-lg border border-gray-300 bg-white shadow-lg'>
                      {/* Search Input */}
                      <div className='border-b border-gray-200 bg-gray-50 p-3'>
                        <div className='relative'>
                          <Search
                            className={cn(
                              "absolute top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400",
                              isHebrew ? "right-3" : "left-3",
                            )}
                          />
                          <input
                            type='text'
                            placeholder={translate("Search by name or email")}
                            className='w-full rounded-md border border-gray-300 py-2 pl-9 pr-8 text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500'
                            value={userSearchQuery}
                            onChange={(e) => setUserSearchQuery(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          {userSearchQuery && (
                            <button
                              onClick={() => setUserSearchQuery("")}
                              className='absolute right-2 top-1/2 -translate-y-1/2 transform text-gray-400 hover:text-gray-600'
                            >
                              <X className='h-4 w-4' />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* User List */}
                      <div className='max-h-64 overflow-y-auto'>
                        {filteredUsers.length === 0 ? (
                          <div className='p-4 text-center text-sm text-gray-500'>
                            {translate("No users found")}
                          </div>
                        ) : (
                          filteredUsers.map((u) => (
                            <button
                              key={u.id}
                              type='button'
                              onClick={() => {
                                setTargetUserId(u.id)
                                setShowUserDropdown(false)
                                setUserSearchQuery("")
                              }}
                              className={cn(
                                "w-full border-b border-gray-100 px-4 py-3 transition-colors last:border-b-0 hover:bg-blue-50",
                                targetUserId === u.id
                                  ? "bg-blue-50 font-medium text-blue-600"
                                  : "text-gray-700",
                                isHebrew ? "text-right" : "text-left",
                              )}
                            >
                              <div className='font-medium'>
                                {u.name || u.email || `User ${u.id}`}
                              </div>
                              {u.email && u.name && (
                                <div className='mt-0.5 text-xs text-gray-500'>{u.email}</div>
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
                {/* Projects & Tasks Column */}
                <div className='rounded-lg border border-gray-200 bg-gray-50 p-5'>
                  <div className='mb-4 flex items-center justify-between'>
                    <h4 className='text-lg font-bold text-gray-800'>
                      {translate("Projects & Tasks")}
                    </h4>
                    <span className='rounded-full bg-blue-100 px-3 py-1 text-sm font-semibold text-blue-700'>
                      {projects.length} {translate("projects")}
                    </span>
                  </div>
                  <div className='max-h-96 space-y-3 overflow-y-auto pr-2'>
                    {projects.length === 0 && orphanTasks.length === 0 && (
                      <div className='py-8 text-center text-gray-500'>
                        {translate("No projects or tasks available")}
                      </div>
                    )}

                    {/* Projects with tasks */}
                    {projects.map((project) => {
                      const projectTasks = tasks.filter((t) => t.project_id === project.id)
                      const checkState = getProjectCheckState(project.id)
                      const isIndeterminate = checkState === "some"
                      const isChecked = checkState === "all" || checkState === "some"

                      return (
                        <div
                          key={project.id}
                          className='rounded-lg border border-gray-300 bg-white shadow-sm transition-shadow hover:shadow-md'
                        >
                          <div className='p-4'>
                            <div className='flex items-start gap-3'>
                              {projectTasks.length > 0 && (
                                <button
                                  onClick={() => toggleProjectExpanded(project.id)}
                                  className='mt-1 text-gray-600 transition-colors hover:text-blue-600'
                                >
                                  {project.expanded ? (
                                    <ChevronDown className='h-5 w-5' />
                                  ) : (
                                    <ChevronRight className='h-5 w-5' />
                                  )}
                                </button>
                              )}
                              {projectTasks.length === 0 && <div className='w-5' />}

                              <label className='flex flex-1 cursor-pointer items-start gap-3'>
                                <div className='relative mt-1'>
                                  <input
                                    type='checkbox'
                                    checked={isChecked}
                                    ref={(el) => {
                                      if (el) {
                                        el.indeterminate = isIndeterminate
                                      }
                                    }}
                                    onChange={(e) => toggleProject(project.id, e.target.checked)}
                                    disabled={!isAdminOrOwner}
                                    className={`h-4 w-4 rounded focus:ring-2 ${
                                      !isAdminOrOwner ? "cursor-not-allowed opacity-50" : ""
                                    }`}
                                  />
                                </div>
                                <div className='flex-1'>
                                  <span className='font-semibold text-gray-800'>
                                    {project.title ??
                                      project.project_number ??
                                      `Project ${project.id}`}
                                  </span>
                                  {projectTasks.length > 0 && (
                                    <span className='ml-2 text-xs text-gray-500'>
                                      ({projectTasks.length} {translate("tasks")})
                                    </span>
                                  )}
                                </div>
                              </label>
                            </div>

                            {project.expanded && projectTasks.length > 0 && (
                              <div className='ml-8 mt-3 space-y-2 border-l-2 border-blue-200 pl-4'>
                                {projectTasks.map((task) => (
                                  <label
                                    key={task.id}
                                    className='flex cursor-pointer items-center gap-3 rounded p-2 transition-colors hover:bg-blue-50'
                                  >
                                    <input
                                      type='checkbox'
                                      checked={task.selected ?? true}
                                      onChange={(e) => toggleTask(task.id, e.target.checked)}
                                      disabled={!isAdminOrOwner}
                                      className={`h-4 w-4 rounded focus:ring-2 ${
                                        !isAdminOrOwner ? "cursor-not-allowed opacity-50" : ""
                                      }`}
                                    />
                                    <span className='text-sm text-gray-700'>
                                      {task.title ?? `Task ${task.id}`}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}

                    {/* Orphan Tasks */}
                    {orphanTasks.length > 0 && (
                      <div className='rounded-lg border border-amber-300 bg-amber-50 shadow-sm'>
                        <div className='p-4'>
                          <div className='mb-3 flex items-center gap-2'>
                            <svg
                              className='h-5 w-5 text-amber-600'
                              fill='none'
                              stroke='currentColor'
                              viewBox='0 0 24 24'
                            >
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                              />
                            </svg>
                            <h5 className='font-semibold text-amber-800'>
                              {translate("Individual Tasks")} ({orphanTasks.length})
                            </h5>
                          </div>
                          <div className='space-y-2 pl-2'>
                            {orphanTasks.map((task) => (
                              <label
                                key={task.id}
                                className='flex cursor-pointer items-center gap-3 rounded p-2 transition-colors hover:bg-amber-100'
                              >
                                <input
                                  type='checkbox'
                                  checked={task.selected ?? true}
                                  onChange={(e) => toggleTask(task.id, e.target.checked)}
                                  disabled={!isAdminOrOwner}
                                  className={`h-4 w-4 rounded focus:ring-2 ${
                                    !isAdminOrOwner ? "cursor-not-allowed opacity-50" : ""
                                  }`}
                                />
                                <span className='text-sm text-gray-700'>
                                  {task.title ?? `Task ${task.id}`}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Roles & Settings Column */}
                <div className='space-y-6'>
                  {/* Roles */}
                  <div className='rounded-lg border border-gray-200 bg-gray-50 p-5'>
                    <div className='mb-4 flex items-center justify-between'>
                      <h4 className='text-lg font-bold text-gray-800'>{translate("Roles")}</h4>
                      <span className='rounded-full bg-purple-100 px-3 py-1 text-sm font-semibold text-purple-700'>
                        {roles.length} {translate("roles")}
                      </span>
                    </div>

                    <div className='max-h-48 space-y-2 overflow-y-auto pr-2'>
                      {roles.length === 0 && (
                        <div className='py-6 text-center text-gray-500'>
                          {translate("No roles available")}
                        </div>
                      )}
                      {roles.map((role) => (
                        <label
                          key={role.id}
                          className='flex cursor-pointer items-center gap-3 rounded-lg border border-transparent p-3 transition-colors hover:border-purple-200 hover:bg-white'
                        >
                          <input
                            type='checkbox'
                            checked={role.selected ?? true}
                            onChange={(e) => toggleRole(role.id, e.target.checked)}
                            disabled={!isAdminOrOwner}
                            className={`h-4 w-4 rounded focus:ring-2 ${
                              !isAdminOrOwner ? "cursor-not-allowed opacity-50" : ""
                            }`}
                          />
                          <span className='font-medium text-gray-700'>
                            {role.name ?? `Role ${role.id}`}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Additional Settings */}
                  <div className='rounded-lg border border-gray-200 bg-gray-50 p-5'>
                    <h4 className='mb-4 text-lg font-bold text-gray-800'>
                      {translate("Settings")}
                    </h4>

                    {/* Toggle Switch for Force Pin */}
                    <div className='mb-4 flex items-center justify-between rounded-lg border border-gray-300 bg-white p-3'>
                      <div>
                        <span className='block font-semibold text-gray-800'>
                          {translate("Force Pin")}
                        </span>
                        <span className='text-xs text-gray-500'>
                          {translate("Pin transferred items by default")}
                        </span>
                      </div>
                      <Switch
                        checked={forcePin}
                        disabled={!isAdminOrOwner}
                        onCheckedChange={() => {
                          if (!isAdminOrOwner) return
                          setForcePin(!forcePin)
                        }}
                      />
                    </div>

                    {/* Custom Date Picker */}
                    <div>
                      <label className='mb-2 block text-sm font-semibold text-gray-700'>
                        {translate("Notification Period")}
                      </label>
                      <div className='relative'>
                        <DatePicker
                          selected={selectedDate}
                          onChange={handleDateChange}
                          dateFormat='yyyy-MM-dd'
                          placeholderText={translate("Select date")}
                          className={cn(
                            "w-full cursor-pointer rounded-lg border border-gray-300 p-3 transition-all focus:border-transparent focus:ring-2 focus:ring-blue-500",
                            !isAdminOrOwner ? "cursor-not-allowed bg-gray-100 opacity-50" : "",
                            isHebrew ? "rtl pr-10" : "ltr pl-10",
                          )}
                          isClearable
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode='select'
                          maxDate={new Date()}
                          disabled={!isAdminOrOwner}
                        />

                        <Calendar
                          className={cn(
                            "pointer-events-none absolute top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400",
                            isHebrew ? "right-3" : "left-3",
                          )}
                        />
                      </div>
                      <p className='mt-2 text-xs text-gray-500'>
                        {translate(
                          "Notifications from this date onwards will be transferred (past dates only)",
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className='flex items-center justify-end gap-3 border-t bg-gray-50 px-6 py-4'>
          {!isAdminOrOwner && (
            <div className='mr-auto flex items-center gap-2 text-amber-600'>
              <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
                />
              </svg>
              <span className='text-sm font-medium'>
                {`${translate("View Only")}: ${translate("Admin or Owner access required")}`}
              </span>
            </div>
          )}
          <button
            onClick={() => !loading && onClose()}
            className='rounded-lg border-2 border-gray-300 px-6 py-2.5 font-semibold text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50'
            disabled={loading || showLoader}
          >
            {isAdminOrOwner ? translate("Cancel") : translate("Close")}
          </button>
          {isAdminOrOwner && (
            <button
              onClick={handleTransfer}
              className='flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-2.5 font-semibold text-white shadow-md transition-all hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60'
              disabled={loading || showLoader}
            >
              {loading ? (
                <>
                  <Loader2 className='h-4 w-4 animate-spin' />
                  {translate("Transferring")}...
                </>
              ) : (
                translate("Transfer Access")
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default TransferAccessModal
